import {
  Component,
  OnInit,
  signal,
  effect,
  inject,
  model,
  OnDestroy,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { AppService } from '@shared/services/app.service';
import { ComandaApiService } from '@core/services/api/comanda-api.service';
import {
  ActualizarEstadoProductoComandaReq,
  ActualizarMesasPosReq,
  ComandaAgregarItemsRes,
  ComandaRes,
  EliminarProductoComandaReq,
  MesaRes,
} from '@core/interfaces/request-response/comanda.dto';
import { EComandaMesaEstado } from '@core/enums/comanda.enum';
import { UiDialog } from '@shared/components/ui';
import { AgregarMesa } from './agregar-mesa/agregar-mesa';
import { SucursalService } from '@shared/services/sucursal.service';
import { ToastService } from '@shared/services/toast.service';
import { VerComanda } from './ver-comanda/ver-comanda';
import { CardComanda } from '@shared/components/card-comanda/card-comanda';
import { estadoGeneralMesaPorProductos } from '@shared/utils/functions.utils';
import { TipoPagoApiService } from '@core/services/api/tipo-pago-api.service';
import { TipoPagoService } from '@shared/services/tipo-pago.service';
import { ComandaListenerService } from '@shared/services/websocket/comanda-listener.service';
import { EVENT_GATEWAY } from '@core/enums/eventWebSocket.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IEventNotification } from '@core/interfaces/socket.interface';

@Component({
  selector: 'app-comandas',
  imports: [CommonModule, UiDialog, AgregarMesa, DragDropModule, VerComanda, CardComanda],
  templateUrl: './comandas.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './comandas.css',
})
export class Comandas implements OnInit, OnDestroy {
  constructor() {
    effect(() => {
      if (this.appService.authReady() && this.sucursalService.sucursalReady()) {
        this.verificarCajaAbierta();
      }
    });
  }
  // servicios
  private readonly appService = inject(AppService);
  private readonly sucursalService = inject(SucursalService);
  private readonly comandaApiService = inject(ComandaApiService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly comandaListenerService = inject(ComandaListenerService);
  private readonly tipoPagoApiService = inject(TipoPagoApiService);
  private readonly tipoPagoService = inject(TipoPagoService);

  // datos
  mesas = signal<MesaRes[]>([]);
  mesa_seleccionada = model<MesaRes | null>(null);
  cajaAbierta = signal<boolean>(false);
  EComandaMesaEstado = EComandaMesaEstado;
  modal_crear_comanda = signal(false);
  modal_ver_comanda = signal(false);
  modal_editar_mesa = signal(false);
  mesa_a_editar = signal<MesaRes | null>(null);
  modoEdicion = signal(false);
  mesasOrdenOriginal = signal<MesaRes[]>([]);

  /**
   * Mapa de handlers para eventos del WebSocket
   * Mejora: Evita switch statement largo y permite extensión fácil
   */
  private readonly eventHandlers: Partial<
    Record<EVENT_GATEWAY, (notification: IEventNotification) => void>
  > = {
    [EVENT_GATEWAY.CONNECT]: () => this.cargarMesas(),
    [EVENT_GATEWAY.COMANDA_MESA_NUEVA]: (n) => this.onCrearMesa(n.data as MesaRes),
    [EVENT_GATEWAY.COMANDA_MESA_ACTUALIZADA]: (n) => this.onActualizarMesa(n.data as MesaRes),
    [EVENT_GATEWAY.COMANDA_MESA_LIMPIADA]: (n) => this.onLimpiarMesa(n.data as number),
    [EVENT_GATEWAY.COMANDA_MESA_ACTUALIZAR_POSICIONES]: (n) =>
      this.onCambiarPosicionesMesas(n.data as ActualizarMesasPosReq),
    [EVENT_GATEWAY.COMANDA_AGREGAR_ITEMS]: (n) =>
      this.onAgregarProductos(n.data as ComandaAgregarItemsRes),
    [EVENT_GATEWAY.COMANDA_ELIMINAR_ITEM]: (n) =>
      this.onEliminarProducto(n.data as EliminarProductoComandaReq),
    [EVENT_GATEWAY.COMANDA_ACTUALIZAR_ESTADO]: (n) =>
      this.onActualizarEstadoProductosComanda(n.data as ActualizarEstadoProductoComandaReq),
    [EVENT_GATEWAY.COMANDA_NUEVA]: (n) => this.onActualizarComanda(n.data as ComandaRes),
    [EVENT_GATEWAY.COMANDA_ACTUALIZADA]: (n) => this.onActualizarComanda(n.data as ComandaRes),
  };

  estadoMesa(mesa: MesaRes): EComandaMesaEstado {
    return estadoGeneralMesaPorProductos(mesa.comanda?.productos || []);
  }

  // métodos
  private cargarMesas() {
    this.comandaApiService.cargarMesas().subscribe({
      next: (mesas) => {
        const mesasOrdenadas = [...mesas].sort((a, b) => a.numero - b.numero);
        this.mesas.set(mesasOrdenadas);

        const mesaSeleccionadaId = this.mesa_seleccionada()?.id;
        if (mesaSeleccionadaId) {
          const mesaActualizada =
            mesasOrdenadas.find((mesa) => mesa.id === mesaSeleccionadaId) || null;
          this.mesa_seleccionada.set(mesaActualizada);
        }
      },
      error: (error) => {
        console.error('Error al cargar mesas:', error);
      },
    });
  }

  private cargarTiposPago() {
    this.tipoPagoApiService.cargarActivos().subscribe((response) => {
      this.tipoPagoService.setTiposPago(response);
    });
  }

  verComanda(mesa: MesaRes | null) {
    this.modal_ver_comanda.set(!!mesa);
    this.mesa_seleccionada.set(mesa);
  }

  abrirEditarMesa(mesa: MesaRes) {
    this.mesa_a_editar.set(mesa);
    this.modal_editar_mesa.set(true);
  }

  onCerrarEditarMesa(mesaActualizada?: MesaRes) {
    this.modal_editar_mesa.set(false);
    if (mesaActualizada) {
      this.onActualizarMesa(mesaActualizada);
    }
  }

  onCrearMesa(mesa: MesaRes) {
    this.mesas.update((mesas) => [...mesas, mesa]);
    this.modal_crear_comanda.set(false);
  }

  /**
   * Utilidad para actualizar una mesa en la lista
   * Mejora: Reduce boilerplate duplicado en múltiples handlers
   */
  private actualizarMesaEnLista(mesaRef: MesaRes, updateFn: (mesa: MesaRes) => MesaRes): void {
    this.mesas.update((mesas) => {
      const index = mesas.findIndex((m) => m.id === mesaRef.id);
      if (index === -1) return mesas;

      const mesasActualizadas = [...mesas];
      const mesaActualizada = updateFn(mesas[index]);
      mesasActualizadas[index] = mesaActualizada;
      this.updateSeleccionadaComanda(mesaActualizada);
      return mesasActualizadas;
    });
  }

  onActualizarMesa(mesa: MesaRes) {
    this.actualizarMesaEnLista(mesa, (m) => ({
      ...m,
      ...mesa,
      estado: this.estadoMesa(mesa),
    }));
  }

  onLimpiarMesa(mesa_id: number) {
    this.actualizarMesaEnLista({ id: mesa_id } as MesaRes, (m) => ({
      ...m,
      comanda: null,
      estado: EComandaMesaEstado.LIBRE,
    }));
  }

  onActualizarComanda(comanda: ComandaRes) {
    this.actualizarMesaEnLista({ id: comanda.mesa_id } as MesaRes, (m) => {
      const mesaActualizada = { ...m, comanda };
      return { ...mesaActualizada, estado: this.estadoMesa(mesaActualizada) };
    });
  }

  onCambiarPosicionesMesas(rq: ActualizarMesasPosReq) {
    this.mesas.update((mesas) =>
      mesas.map((mesa) => {
        const posicionActualizada = rq.mesas.find((m) => m.id === mesa.id);
        return posicionActualizada ? { ...mesa, numero: posicionActualizada.numero } : mesa;
      }),
    );
  }

  onAgregarProductos(comandaAgregarItems: ComandaAgregarItemsRes) {
    this.actualizarMesaEnLista({ id: comandaAgregarItems.mesa_id } as MesaRes, (m) => {
      const comanda = m.comanda || { id: 0, clientes: 0, productos: [], mesa_id: m.id };
      const mesaActualizada = {
        ...m,
        comanda: {
          ...comanda,
          productos: [...comanda.productos, ...comandaAgregarItems.items],
        },
      };
      return { ...mesaActualizada, estado: this.estadoMesa(mesaActualizada) };
    });
  }

  onActualizarEstadoProductosComanda(rq: ActualizarEstadoProductoComandaReq) {
    this.actualizarMesaEnLista({ id: rq.mesa_id } as MesaRes, (m) => {
      if (!m.comanda) return m;

      const productosActualizados = m.comanda.productos.map((item) =>
        rq.comandas_items_ids.includes(item.id) ? { ...item, estado: rq.estado } : item,
      );

      const mesaActualizada = {
        ...m,
        comanda: { ...m.comanda, productos: productosActualizados },
      };
      return { ...mesaActualizada, estado: this.estadoMesa(mesaActualizada) };
    });
  }

  onEliminarProducto(rq: EliminarProductoComandaReq) {
    this.actualizarMesaEnLista({ id: rq.mesa_id } as MesaRes, (m) => {
      if (!m.comanda) return m;

      const mesaActualizada = {
        ...m,
        comanda: {
          ...m.comanda,
          productos: m.comanda.productos.filter((item) => item.id !== rq.comanda_item_id),
        },
      };
      return { ...mesaActualizada, estado: this.estadoMesa(mesaActualizada) };
    });
  }

  private updateSeleccionadaComanda(mesa: MesaRes): void {
    if (this.mesa_seleccionada()?.id === mesa.id) {
      this.mesa_seleccionada.set(mesa);
    }
  }

  toggleModoEdicion() {
    if (!this.modoEdicion()) {
      this.mesasOrdenOriginal.set([...this.mesas()]);
    }
    this.modoEdicion.update((modo) => !modo);
  }

  onDrop(event: CdkDragDrop<MesaRes[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const mesasArray = [...this.mesas()];
    moveItemInArray(mesasArray, event.previousIndex, event.currentIndex);
    this.mesas.set(mesasArray);
  }

  guardarCambiosOrden() {
    const mesasActuales = this.mesas();
    const mesasOriginales = this.mesasOrdenOriginal();

    const hayChangios = mesasActuales.some((mesa, index) => mesa.id !== mesasOriginales[index]?.id);

    if (!hayChangios) {
      this.toastService.show(
        'top-right',
        'info',
        'Sin cambios',
        'No hay cambios en el orden de las mesas.',
      );
      this.modoEdicion.set(false);
      return;
    }

    const rq: ActualizarMesasPosReq = {
      mesas: mesasActuales.map((m, i) => ({ id: m.id, numero: i })),
    };

    this.comandaApiService.actualizarPosicionesMesas(rq).subscribe({
      next: () => {
        this.toastService.show(
          'top-right',
          'success',
          'Orden guardado',
          'El nuevo orden de las mesas ha sido guardado exitosamente.',
        );
        this.modoEdicion.set(false);
        this.mesasOrdenOriginal.set([]);
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo guardar el nuevo orden de las mesas.',
        );
        console.error('Error al actualizar posiciones de mesas:', err);
      },
    });
  }

  cancelarCambiosOrden() {
    this.mesas.set([...this.mesasOrdenOriginal()]);
    this.modoEdicion.set(false);
    this.mesasOrdenOriginal.set([]);
    this.toastService.show(
      'top-right',
      'info',
      'Edición cancelada',
      'Se han descartado los cambios realizados.',
    );
  }

  private verificarCajaAbierta() {
    this.cajaAbierta.set(true);
    this.cargarMesas();
    this.cargarTiposPago();
    this.comandaListenerService.start(this.sucursalService.activeSucursal()!.id);
  }

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Comandas',
      subtitle: 'Gestión de mesas y comandas',
      showBackButton: true,
      icon: 'bi bi-list-task',
    });

    this.comandaListenerService.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((notification) => {
        this.handleWebSocketEvent(notification);
      });
  }

  /**
   * Maneja eventos del WebSocket de forma centralizada
   * Mejora: Usa mapa de handlers para evitar switch statement
   * @param notification Notificación recibida
   */
  private handleWebSocketEvent(notification: IEventNotification): void {
    const handler = this.eventHandlers[notification.event];
    handler?.(notification);
  }

  ngOnDestroy(): void {
    const sucursalId = this.sucursalService.activeSucursal()?.id;
    if (sucursalId) {
      this.comandaListenerService.stop(sucursalId);
    }
  }
}
